import { useMemo, useState } from "react";
import clsx from "clsx";
import { eqByKind, toISO, parseNum } from "../../lib/fieldEquality";
import {
  FIELD_SPEC,
  FIELD_GROUPS,
  type FieldDef,
  type FieldGroup,
} from "./fieldSpec";
import type {
  ExtractionResult,
  DealPicklists,
  CreatePayload,
  EnrichPayload,
} from "../../types/intake";

interface Props {
  variant: "create" | "enrich";
  extraction: ExtractionResult;
  picklists: DealPicklists;
  /** Enrich only — current Zoho field values (conflict source). */
  current?: Record<string, unknown>;
  dealId?: string;
  onCreate?: (p: CreatePayload) => Promise<void>;
  onEnrich?: (p: EnrichPayload) => Promise<void>;
  onCancel: () => void;
  /** Fired on the first user edit — lets a host modal arm its dismiss guardrail. */
  onDirty?: () => void;
}

type ConflictState = "empty" | "new" | "current_only" | "match" | "conflict";

// Client mirror of the Worker's extractCity / autoDealName (must stay in sync).
const STREET_SUFFIX =
  /\b(st|street|dr|drive|ave|avenue|rd|road|blvd|boulevard|ln|lane|ct|court|way|pl|place|cir|circle|ter|terrace|hwy|highway|pkwy|parkway|trl|trail|loop|run|path)\b\.?/i;

function extractCity(loc: string): string {
  const parts = loc.split(",").map((s) => s.trim()).filter(Boolean);
  let cand =
    parts.length >= 3 ? parts[parts.length - 2] : parts.length === 2 ? parts[0] : parts[0] ?? "";
  const m = cand.match(STREET_SUFFIX);
  if (m && m.index !== undefined) {
    const after = cand.slice(m.index + m[0].length).trim();
    if (after) cand = after;
  }
  cand = cand.replace(/^\d+\s+/, "").trim();
  cand = cand.replace(/\s+[A-Z]{2}(\s+\d{5}(-\d{4})?)?$/, "").trim();
  return cand;
}

/** Deal-name BASE: "{Borrower} - {City}". The loan # is prepended by the worker
 *  on save (Zoho assigns it at create); the preview shows "[loan #] - base". */
function autoDealName(fields: Record<string, unknown>, borrowerName?: string): string {
  const city = extractCity(String(fields.Property_Location ?? ""));
  const name = (borrowerName ?? "").trim();
  return [name, city].filter(Boolean).join(" - ") || "New Deal";
}
function todayPlusDays(n: number): string {
  return new Date(Date.now() + n * 86_400_000).toISOString().slice(0, 10);
}

function toInputStr(v: unknown, kind: FieldDef["kind"]): string {
  if (v == null) return "";
  if (kind === "date") return toISO(String(v)) ?? String(v);
  return String(v);
}

/** Thousands separators for display only — state stores raw digits. */
function formatThousands(s: string): string {
  const digits = s.replace(/[^\d]/g, "");
  if (!digits) return "";
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

/**
 * Edge-computed CLTV (the AI never produces it): (Σ existing debts NOT being
 * paid off + new loan) ÷ Projected_Value × 100, to 1 decimal. Null when there's
 * no property value (Projected_Value blank → show "—", no ARV fallback yet).
 */
function computeCltv(
  vals: Record<string, string>,
  bools: Record<string, boolean>,
): number | null {
  const value = parseNum(vals.Projected_Value ?? "");
  if (value == null || value <= 0) return null;
  const debt = (amtKey: string, payoffKey: string): number => {
    if (bools[payoffKey]) return 0; // paid off → excluded
    return parseNum(vals[amtKey] ?? "") ?? 0;
  };
  const kept =
    debt("st_Mortgage_Amount", "st_Mortgage_Pay_Off") +
    debt("nd_Mortgage_Amount", "nd_Mortgage_Pay_Off") +
    debt("rd_Mortgage_Amount", "rd_Mortgage_Pay_Off");
  const newLoan = parseNum(vals.Loan_Amount_Requested ?? "") ?? 0;
  return Math.round(((kept + newLoan) / value) * 1000) / 10;
}

function isMissing(v: unknown): boolean {
  return v == null || (typeof v === "string" && v.trim() === "");
}

interface FieldModel extends FieldDef {
  extractedStr: string;
  currentStr: string;
  confidence?: string; // "high" | "medium" | "low"
  isDefault: boolean; // create — value came from a default, not capture
  state: ConflictState; // enrich (string fields)
}

/**
 * IntakeReview — the mandatory write-gate. Renders every writable field,
 * editable, grouped. Create: flat form + underwriter note. Enrich: amber
 * conflict rows for hard fields that differ from current (default keep-current),
 * the note as a new Call Note, and a previewed opt-in for Loan_Request_Notes.
 * Parent owns the worker call (onCreate / onEnrich).
 */
export function IntakeReview({
  variant,
  extraction,
  picklists,
  current = {},
  dealId,
  onCreate,
  onEnrich,
  onCancel,
  onDirty,
}: Props) {
  const isEnrich = variant === "enrich";

  // ── Per-field models ──
  const models: FieldModel[] = useMemo(() => {
    return FIELD_SPEC.map((f) => {
      const exRaw = extraction.fields[f.key];
      const curRaw = current[f.key];
      let extractedStr = toInputStr(exRaw, f.kind);
      const currentStr = toInputStr(curRaw, f.kind);
      let isDefault = false;

      // Create defaults (visible + marked).
      if (!isEnrich && isMissing(exRaw)) {
        if (f.key === "Deal_Name") {
          extractedStr = autoDealName(extraction.fields);
          isDefault = true;
        } else if (f.key === "Stage") {
          extractedStr = "Active Deals";
          isDefault = true;
        } else if (f.key === "Closing_Date") {
          extractedStr = todayPlusDays(45);
          isDefault = true;
        }
      }

      let state: ConflictState = "new";
      if (isEnrich && f.kind !== "boolean") {
        const hasEx = !isMissing(exRaw);
        const hasCur = !isMissing(curRaw);
        if (!hasEx && !hasCur) state = "empty";
        else if (hasEx && !hasCur) state = "new";
        else if (!hasEx && hasCur) state = "current_only";
        else state = eqByKind(f.kind, extractedStr, currentStr) ? "match" : "conflict";
      }

      return {
        ...f,
        extractedStr,
        currentStr,
        confidence: extraction.confidence[f.key],
        isDefault,
        state,
      };
    });
  }, [extraction, current, isEnrich]);

  // ── Editable state ──
  const [vals, setVals] = useState<Record<string, string>>(() => {
    const seed: Record<string, string> = {};
    for (const m of models) {
      if (m.kind === "boolean") continue;
      // Enrich seeds conflicts/match/current_only to CURRENT (safe); new to extracted.
      seed[m.key] =
        isEnrich && (m.state === "conflict" || m.state === "match" || m.state === "current_only")
          ? m.currentStr
          : m.extractedStr;
    }
    return seed;
  });
  const [bools, setBools] = useState<Record<string, boolean>>(() => {
    const seed: Record<string, boolean> = {};
    for (const m of models) {
      if (m.kind !== "boolean") continue;
      const src = isEnrich ? current[m.key] : extraction.fields[m.key];
      seed[m.key] = src === true || src === "true";
    }
    return seed;
  });
  const [note, setNote] = useState(extraction.note ?? "");

  // Loan_Request_Notes opt-in (enrich only).
  const lrnModel = models.find((m) => m.key === "Loan_Request_Notes")!;
  const lrnProposed = lrnModel.extractedStr.trim();
  const lrnCurrent = lrnModel.currentStr.trim();
  const lrnDiffers = isEnrich && lrnProposed.length > 0 && lrnProposed !== lrnCurrent;
  const [lrnOptIn, setLrnOptIn] = useState(false);
  const [lrnText, setLrnText] = useState(lrnProposed);

  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const borrowerName = [vals.First_Name, vals.Last_Name]
    .filter((s) => s && s.trim())
    .join(" ")
    .trim();
  // Read-only, derived. Create preview: "[loan #] - Borrower - City" (Zoho
  // assigns the loan # on save). Enrich: the existing name, unchanged.
  const dealNameBase = autoDealName({ Property_Location: vals.Property_Location }, borrowerName);
  const dealNamePreview = isEnrich ? vals.Deal_Name ?? "" : `[loan #] - ${dealNameBase}`;

  const cltv = computeCltv(vals, bools);

  const setVal = (k: string, v: string) => {
    onDirty?.();
    setVals((p) => ({ ...p, [k]: v }));
  };
  const setBool = (k: string, v: boolean) => {
    onDirty?.();
    setBools((p) => ({ ...p, [k]: v }));
  };

  // ── Enrich change summary ──
  const summary = useMemo(() => {
    if (!isEnrich) return { newCount: 0, changeCount: 0, unchangedCount: 0 };
    let n = 0, c = 0, u = 0;
    for (const m of models) {
      if (m.gatedInEnrich || m.readOnlyInEnrich) continue;
      if (m.kind === "boolean") {
        const accepted = bools[m.key] ?? false;
        const cur = current[m.key] === true || current[m.key] === "true";
        if (accepted !== cur) c++;
        continue;
      }
      const v = (vals[m.key] ?? "").trim();
      if (!v && !m.currentStr) continue;
      if (!m.currentStr && v) { n++; continue; }
      if (m.currentStr && !v) { u++; continue; }
      if (eqByKind(m.kind, v, m.currentStr)) u++; else c++;
    }
    return { newCount: n, changeCount: c, unchangedCount: u };
  }, [isEnrich, models, vals, bools, current]);

  // Read-only (Deal_Name) and gated (Loan_Request_Notes) fields are never
  // editable in enrich and must never count as — or render as — a conflict.
  const conflictCount = isEnrich
    ? models.filter((m) => m.state === "conflict" && !m.readOnlyInEnrich && !m.gatedInEnrich).length
    : 0;

  function buildCreate(): CreatePayload {
    const fields: Record<string, unknown> = {};
    const contact: Record<string, string> = {};
    for (const m of models) {
      if (m.key === "Deal_Name") continue; // worker derives the name (loan # + base)
      if (m.kind === "boolean") { fields[m.key] = bools[m.key] ?? false; continue; }
      const v = (vals[m.key] ?? "").trim();
      if (!v) continue;
      if (m.target === "contact") contact[m.key] = v;
      else fields[m.key] = v;
    }
    // Deal_Name is NOT sent — the worker derives the base + prepends the loan #
    // after create so the name matches the WorkDrive folder.
    if (cltv != null) fields.CLTV = cltv; // Edge-computed, not AI
    return {
      fields,
      note: note.trim(),
      contact: Object.keys(contact).length > 0 ? contact : undefined,
    };
  }

  function buildEnrich(): EnrichPayload {
    const fields: Record<string, unknown> = {};
    for (const m of models) {
      if (m.gatedInEnrich || m.readOnlyInEnrich) continue; // LRN gated; Deal_Name frozen
      if (m.kind === "boolean") {
        const accepted = bools[m.key] ?? false;
        const cur = current[m.key] === true || current[m.key] === "true";
        if (accepted !== cur) fields[m.key] = accepted;
        continue;
      }
      const v = (vals[m.key] ?? "").trim();
      if (!v) continue;
      if (m.currentStr && eqByKind(m.kind, v, m.currentStr)) continue; // unchanged
      fields[m.key] = v;
    }
    if (cltv != null) {
      const cur = current.CLTV;
      const curNum = cur == null ? null : Math.round(Number(cur) * 10) / 10;
      if (curNum !== cltv) fields.CLTV = cltv; // recomputed from merged debt/value
    }
    return {
      deal_id: dealId!,
      fields,
      call_note: note.trim() || undefined,
      loan_request_notes: lrnOptIn && lrnText.trim() ? lrnText.trim() : undefined,
    };
  }

  async function handleSubmit() {
    setSubmitting(true);
    setError(null);
    try {
      if (isEnrich) await onEnrich?.(buildEnrich());
      else await onCreate?.(buildCreate());
    } catch (err) {
      setError(err instanceof Error ? err.message : "submit_failed");
      setSubmitting(false);
    }
  }

  return (
    <div className="intake-review">
      <div className="intake-review-head">
        <span className="intake-review-title">
          {isEnrich ? "ADD TO DEAL" : "REVIEW NEW DEAL"}
        </span>
        <span className="intake-review-dealname">{dealNamePreview || "—"}</span>
      </div>

      {/* Create: read-only derived name (matches the WorkDrive folder). The loan
          # is assigned by Zoho on save; edit borrower/property to change it. */}
      {!isEnrich && (
        <div className="intake-dealname-field">
          <div className="intake-field-head">
            <span className="intake-field-label">Deal name</span>
            <span className="intake-default-chip">auto · matches WorkDrive folder</span>
          </div>
          <div className="intake-readonly">{dealNamePreview}</div>
          <span className="intake-dealname-hint">
            Zoho assigns the loan # on save. Edit the borrower or property to change this.
          </span>
        </div>
      )}

      {isEnrich && conflictCount > 0 && (
        <div className="cl-conflict-banner">
          ⚠ {conflictCount} field{conflictCount === 1 ? "" : "s"} differ from the
          current deal. Each defaults to <em>keep current</em> — switch to the new
          value only where it's right.
        </div>
      )}

      {extraction.unclear.length > 0 && (
        <details className="intake-unclear">
          <summary>⚑ {extraction.unclear.length} item(s) to verify</summary>
          <ul>
            {extraction.unclear.map((u, i) => (
              <li key={i}>{u}</li>
            ))}
          </ul>
        </details>
      )}

      <div className="intake-fields">
        {FIELD_GROUPS.map((group) => (
          <GroupSection
            key={group}
            group={group}
            models={models.filter(
              (m) =>
                m.group === group &&
                !(isEnrich && m.gatedInEnrich) &&
                !(isEnrich && m.target === "contact") && // contact = create-only for now
                // Deal_Name never appears in the grid: create shows it in the top
                // preview; enrich owns the loan-number name and is read-only (the
                // deal name is in the breadcrumb) — never re-proposed or a conflict.
                m.key !== "Deal_Name",
            )}
            isEnrich={isEnrich}
            vals={vals}
            bools={bools}
            picklists={picklists}
            onVal={setVal}
            onBool={setBool}
          />
        ))}
      </div>

      {/* CLTV — Edge-computed, read-only, recomputes as debts/loan/value change */}
      <div className="intake-cltv">
        <span className="intake-cltv-label">CLTV (computed)</span>
        <span className="intake-cltv-value">{cltv != null ? `${cltv}%` : "—"}</span>
        <span className="intake-cltv-hint">
          kept debts + loan ÷ property value{cltv == null ? " · needs a property value" : ""}
        </span>
      </div>

      {/* Loan_Request_Notes opt-in (enrich) */}
      {isEnrich && lrnDiffers && (
        <div className="intake-lrn">
          <label className="intake-lrn-toggle">
            <input
              type="checkbox"
              checked={lrnOptIn}
              onChange={(e) => setLrnOptIn(e.target.checked)}
            />
            Refresh the lender paragraph (Loan_Request_Notes)? This text goes
            verbatim into lender outreach.
          </label>
          {lrnOptIn && (
            <>
              <div className="intake-lrn-current">
                <span className="intake-lrn-label">Current</span>
                {lrnCurrent || "—"}
              </div>
              <textarea
                className="intake-textarea"
                value={lrnText}
                onChange={(e) => setLrnText(e.target.value)}
                rows={4}
              />
            </>
          )}
        </div>
      )}

      {/* Note */}
      <div className="intake-note-block">
        <div className="intake-note-label">
          {isEnrich ? "Call notes (added as a new Call Notes note)" : "Intake summary note"}
        </div>
        <textarea
          className="intake-textarea intake-note"
          value={note}
          onChange={(e) => {
            onDirty?.();
            setNote(e.target.value);
          }}
          rows={8}
        />
      </div>

      {error && <div className="write-confirm-error">⚠ {error}</div>}

      {/* Footer */}
      {!confirming ? (
        <div className="intake-actions">
          <button className="write-confirm-cancel" onClick={onCancel} disabled={submitting}>
            CANCEL
          </button>
          <button
            className="write-confirm-confirm"
            onClick={() => (isEnrich ? setConfirming(true) : void handleSubmit())}
            disabled={submitting}
          >
            {submitting ? "SAVING…" : isEnrich ? "REVIEW & SAVE" : "CREATE DEAL"}
          </button>
        </div>
      ) : (
        <div className="cl-confirm-strip">
          <div className="cl-confirm-summary">
            Saving {summary.newCount} new, {summary.changeCount} change
            {summary.changeCount === 1 ? "" : "s"}, {summary.unchangedCount} unchanged
            {note.trim() ? " · + Call Note" : ""}
            {lrnOptIn ? " · + lender paragraph" : ""}.
            {summary.changeCount > 0 && (
              <strong className="cl-confirm-changes">
                {" "}
                {summary.changeCount} existing value
                {summary.changeCount === 1 ? "" : "s"} will be overwritten.
              </strong>
            )}
          </div>
          <div className="cl-confirm-actions">
            <button className="write-confirm-cancel" onClick={() => setConfirming(false)} disabled={submitting}>
              CANCEL
            </button>
            <button className="write-confirm-confirm" onClick={() => void handleSubmit()} disabled={submitting}>
              {submitting ? "SAVING…" : "✓ CONFIRM SAVE"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Group section ──
function GroupSection({
  group,
  models,
  isEnrich,
  vals,
  bools,
  picklists,
  onVal,
  onBool,
}: {
  group: FieldGroup;
  models: FieldModel[];
  isEnrich: boolean;
  vals: Record<string, string>;
  bools: Record<string, boolean>;
  picklists: DealPicklists;
  onVal: (k: string, v: string) => void;
  onBool: (k: string, v: boolean) => void;
}) {
  if (models.length === 0) return null;
  return (
    <div className="intake-group">
      <div className="intake-group-title">{group.toUpperCase()}</div>
      {models.map((m) => (
        <FieldRow
          key={m.key}
          m={m}
          isEnrich={isEnrich}
          value={vals[m.key] ?? ""}
          checked={bools[m.key] ?? false}
          picklists={picklists}
          onVal={onVal}
          onBool={onBool}
        />
      ))}
    </div>
  );
}

// ── One field, by kind + variant ──
function FieldRow({
  m,
  isEnrich,
  value,
  checked,
  picklists,
  onVal,
  onBool,
}: {
  m: FieldModel;
  isEnrich: boolean;
  value: string;
  checked: boolean;
  picklists: DealPicklists;
  onVal: (k: string, v: string) => void;
  onBool: (k: string, v: boolean) => void;
}) {
  const lowConf = m.confidence === "medium" || m.confidence === "low";
  const readOnly = isEnrich && m.readOnlyInEnrich;

  const head = (
    <div className="intake-field-head">
      <span className="intake-field-label">{m.label}</span>
      {m.isDefault && <span className="intake-default-chip">default</span>}
      {lowConf && <span className="intake-verify-badge">verify · {m.confidence}</span>}
    </div>
  );

  // Boolean
  if (m.kind === "boolean") {
    const extractedBool = m.extractedStr === "true";
    const curBool = m.currentStr === "true";
    return (
      <div className="intake-field">
        <label className="intake-checkbox">
          <input
            type="checkbox"
            checked={checked}
            onChange={(e) => onBool(m.key, e.target.checked)}
          />
          {m.label}
          {isEnrich && !m.gatedInEnrich && extractedBool !== curBool && m.extractedStr !== "" && (
            <span className="intake-extracted-hint"> · extracted: {extractedBool ? "yes" : "no"}</span>
          )}
        </label>
      </div>
    );
  }

  // Enrich conflict row
  if (isEnrich && m.state === "conflict") {
    const isKeep = eqByKind(m.kind, value, m.currentStr);
    const isUse = eqByKind(m.kind, value, m.extractedStr);
    return (
      <div className="intake-field cl-row-conflict">
        {head}
        <div className="cl-compare">
          <div className="cl-compare-line">
            <span className="cl-compare-tag">Current</span>
            {m.currentStr || "—"}
          </div>
          <div className="cl-compare-line">
            <span className="cl-compare-tag cl-compare-tag-ex">Extracted</span>
            {m.extractedStr || "—"}
          </div>
        </div>
        <div className="cl-seg">
          <button className={clsx("cl-seg-btn", isKeep && "active")} onClick={() => onVal(m.key, m.currentStr)}>
            KEEP CURRENT
          </button>
          <button className={clsx("cl-seg-btn", isUse && !isKeep && "active")} onClick={() => onVal(m.key, m.extractedStr)}>
            USE EXTRACTED
          </button>
        </div>
        <FieldInput m={m} value={value} picklists={picklists} onVal={onVal} />
      </div>
    );
  }

  // Plain field (create, or non-conflict enrich)
  return (
    <div className="intake-field">
      {head}
      {readOnly ? (
        <div className="intake-readonly">{value || "—"}</div>
      ) : (
        <FieldInput m={m} value={value} picklists={picklists} onVal={onVal} lowConf={lowConf} />
      )}
    </div>
  );
}

function FieldInput({
  m,
  value,
  picklists,
  onVal,
  lowConf,
}: {
  m: FieldModel;
  value: string;
  picklists: DealPicklists;
  onVal: (k: string, v: string) => void;
  lowConf?: boolean;
}) {
  const cls = clsx("intake-input", lowConf && "low-conf");
  if (m.kind === "multiline") {
    return <textarea className={clsx(cls, "intake-textarea")} value={value} rows={3} onChange={(e) => onVal(m.key, e.target.value)} />;
  }
  if (m.kind === "currency") {
    // Display with thousands separators; store raw digits so the write stays numeric.
    return (
      <input
        className={cls}
        inputMode="numeric"
        value={formatThousands(value)}
        onChange={(e) => onVal(m.key, e.target.value.replace(/[^\d]/g, ""))}
      />
    );
  }
  if (m.kind === "picklist" && m.picklistKey) {
    const pk = picklists.fields[m.picklistKey];
    // Free-text picklists (e.g. Loan_Purpose when Zoho type is text) → input.
    if (pk && pk.dataType === "text") {
      return <input className={cls} value={value} onChange={(e) => onVal(m.key, e.target.value)} />;
    }
    const options = pk?.options ?? [];
    return (
      <select className={cls} value={value} onChange={(e) => onVal(m.key, e.target.value)}>
        <option value="">—</option>
        {options.map((o) => (
          <option key={o} value={o}>{o}</option>
        ))}
        {value && !options.includes(value) && <option value={value}>{value} (extracted)</option>}
      </select>
    );
  }
  return (
    <input
      className={cls}
      type={m.kind === "date" ? "date" : "text"}
      value={value}
      onChange={(e) => onVal(m.key, e.target.value)}
    />
  );
}
